import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '../../components/ui/Button.js';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog.js';

/**
 * "Connect Claude Code" walkthrough, opened from the Connected apps card.
 *
 * There is nothing to configure here — no token to generate, no id to copy — because the whole
 * point of the OAuth + DCR flow (see docs/MCP.md) is that `claude mcp add` self-registers and
 * walks the user through a browser consent screen on its own. So this dialog is pure
 * documentation: the one command to run, and what to expect after running it. It exists because
 * "run this command, then look for a browser popup" is not obvious the first time, not because
 * Pulse has any setup state of its own to expose.
 */

const MCP_URL = `${window.location.origin}/mcp`;
const INSTALL_COMMAND = 'npm install -g @anthropic-ai/claude-code';
const MAC_INSTALL_COMMAND = 'curl -fsSL https://claude.ai/install.sh | bash';
// --scope user, not the default local scope: local is keyed on the exact folder path, so a
// registration made from another folder never shows up — and on Windows the VS Code extension and
// a terminal can even spell the same folder with a different drive letter case (c:/ vs C:/).
const ADD_COMMAND = `claude mcp add --transport http --scope user pulse ${MCP_URL}`;

function CodeRow({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded bg-bg px-2 py-1.5 text-xs">
        {text}
      </code>
      <Button type="button" variant="secondary" onClick={() => void copy()} className="shrink-0">
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        <span className="ml-1.5">{copied ? 'Copied' : 'Copy'}</span>
      </Button>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-2 text-xs font-medium">
        {n}
      </span>
      <div className="flex flex-1 flex-col gap-2">
        <p className="text-sm font-medium">{title}</p>
        {children}
      </div>
    </div>
  );
}

export function McpSetupDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} className="max-w-2xl">
      {/* Capped to the viewport (minus the dialog's own margin and padding) so the steps list,
          not the page, scrolls on short screens — header and Done stay in view. */}
      <div className="flex max-h-[calc(100dvh-5.5rem)] flex-col gap-5">
        <DialogHeader>
          <DialogTitle>Connect an AI coding agent</DialogTitle>
          <DialogDescription>
            Lets an agent like Claude Code read this project's specs and tasks, and hand finished
            work back to you as <strong className="text-fg">Testing</strong> — it can never mark
            anything Done. Nothing here is a shared secret; each step is one command or one click.
            Works the same in the terminal and in the VS Code extension.
          </DialogDescription>
        </DialogHeader>

        <div className="-mr-2 flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-2">
          <Step n={1} title="Install the Claude Code CLI">
            <CodeRow text={INSTALL_COMMAND} />
            <p className="text-sm text-muted">
              Needed even if you use the VS Code extension — the extension doesn't put{' '}
              <code className="rounded bg-bg px-1 py-0.5 text-xs">claude</code> on your PATH. Open a
              new terminal afterwards so it's picked up. On macOS, if npm fails with{' '}
              <code className="rounded bg-bg px-1 py-0.5 text-xs">EACCES</code>, don't use{' '}
              <code className="rounded bg-bg px-1 py-0.5 text-xs">sudo</code> — use the native
              installer instead:
            </p>
            <CodeRow text={MAC_INSTALL_COMMAND} />
          </Step>

          <Step n={2} title="Add the server, from any terminal">
            <CodeRow text={ADD_COMMAND} />
            <p className="text-sm text-muted">
              Check it with{' '}
              <code className="rounded bg-bg px-1 py-0.5 text-xs">claude mcp list</code> —{' '}
              <strong>pulse</strong> should be listed with this URL.
            </p>
          </Step>

          <Step n={3} title="Restart VS Code">
            <p className="text-sm text-muted">
              A running Claude Code session won't see a newly added server. Reload the window (
              <code className="rounded bg-bg px-1 py-0.5 text-xs">Ctrl+Shift+P</code>, or{' '}
              <code className="rounded bg-bg px-1 py-0.5 text-xs">Cmd+Shift+P</code> on macOS →{' '}
              <strong>Developer: Reload Window</strong>) or start a new terminal session.
            </p>
          </Step>

          <Step n={4} title="Authenticate">
            <p className="text-sm text-muted">
              Inside Claude Code, run{' '}
              <code className="rounded bg-bg px-1 py-0.5 text-xs">/mcp</code>, pick{' '}
              <strong>pulse</strong>, then <strong>Authenticate</strong>. A browser opens — sign in
              with the same Google account you use for Pulse.
            </p>
          </Step>

          <Step n={5} title="Review and approve">
            <p className="text-sm text-muted">
              You'll see exactly what the agent can do, and can untick any write permission before
              approving. It only reaches projects you're already a member of.
            </p>
          </Step>
        </div>

        <div className="shrink-0 rounded-lg border border-border bg-surface-2 p-3 text-sm text-muted">
          Once connected, it shows up below in <strong className="text-fg">Connected apps</strong> —
          disconnect it there at any time, which takes effect immediately.
        </div>

        <div className="flex shrink-0 justify-end">
          <Button type="button" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
